import React from 'react';
import { Box, LinearProgress, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import {
  evaluatePassword,
  DEFAULT_PASSWORD_POLICY,
  PasswordPolicy,
} from '../../utils/passwordStrength';

const SCORE_COLORS: Record<number, string> = {
  0: '#9e9e9e',
  1: '#d32f2f',
  2: '#ed6c02',
  3: '#1976d2',
  4: '#2e7d32',
};

interface PasswordStrengthMeterProps {
  password: string;
  policy?: PasswordPolicy;
  showChecklist?: boolean;
}

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
  policy = DEFAULT_PASSWORD_POLICY,
  showChecklist = true,
}) => {
  const result = evaluatePassword(password, policy);
  const color = SCORE_COLORS[result.score] || SCORE_COLORS[0];

  const checklist = [
    { ok: result.checks.length, label: `At least ${policy.minLength} characters` },
    { ok: result.checks.uppercase, label: 'Uppercase letter', hide: !policy.requireUppercase },
    { ok: result.checks.lowercase, label: 'Lowercase letter', hide: !policy.requireLowercase },
    { ok: result.checks.number, label: 'Number', hide: !policy.requireNumbers },
    { ok: result.checks.special, label: 'Special character', hide: !policy.requireSpecialChars },
  ].filter((c) => !c.hide);

  if (!password) {
    return null;
  }

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Password strength
        </Typography>
        <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
          {result.label}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={(result.score / 4) * 100}
        sx={{
          height: 6,
          borderRadius: 1,
          bgcolor: 'action.hover',
          '& .MuiLinearProgress-bar': { bgcolor: color },
        }}
      />
      {showChecklist && (
        <List dense disablePadding sx={{ mt: 0.5 }}>
          {checklist.map((item) => (
            <ListItem key={item.label} dense disableGutters sx={{ py: 0 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {item.ok ? (
                  <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                ) : (
                  <Cancel sx={{ fontSize: 16, color: 'text.disabled' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ variant: 'caption', color: item.ok ? 'text.primary' : 'text.secondary' }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default PasswordStrengthMeter;
